import { useState } from "react";
import {
  useAdminYoutubePlaylists,
  useCreatePlaylist,
} from "../../lib/youtube";

/**
 * Multi-select picker for playlist assignment. Lets the editor toggle
 * which series the video belongs to, and create a brand-new series
 * inline without leaving the form.
 *
 * Receives the current `value` (array of playlist IDs) and emits via
 * `onChange`. The parent persists the change on save (Upload + Edit
 * forms).
 */

export function PlaylistAssignment({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const q = useAdminYoutubePlaylists({ limit: 200 });
  const create = useCreatePlaylist();
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const playlists = q.data?.playlists ?? [];

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  const onCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    try {
      const created = await create.mutateAsync({
        title,
        description: "",
        privacyStatus: "private",
      });
      onChange([...value, created.id]);
      setNewTitle("");
      setCreating(false);
    } catch (e) {
      window.alert(
        e instanceof Error ? e.message : "Couldn't create the series.",
      );
    }
  };

  return (
    <div className="yt-pl-assign">
      {q.isLoading ? (
        <p className="yt-admin-empty">Loading series…</p>
      ) : playlists.length === 0 ? (
        <p className="yt-admin-empty">
          No series yet — create the first one below.
        </p>
      ) : (
        <ul className="yt-pl-assign-list">
          {playlists.map((p) => (
            <li key={p.id}>
              <label className="yt-pl-assign-row">
                <input
                  type="checkbox"
                  checked={value.includes(p.id)}
                  onChange={() => toggle(p.id)}
                />
                <span className="yt-pl-assign-title font-display-italic">
                  {p.title}
                </span>
                <span className="yt-pl-assign-meta tabular">
                  {p.itemCount} {p.itemCount === 1 ? "item" : "items"}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      <div className="yt-pl-assign-create">
        {creating ? (
          <div className="yt-pl-assign-create-form">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="New series title…"
              maxLength={150}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onCreate();
                } else if (e.key === "Escape") {
                  setCreating(false);
                  setNewTitle("");
                }
              }}
            />
            <button
              type="button"
              className="btn-app"
              onClick={onCreate}
              disabled={!newTitle.trim() || create.isPending}
            >
              {create.isPending ? "Creating…" : "Create"}
            </button>
            <button
              type="button"
              className="btn-app-ghost"
              onClick={() => {
                setCreating(false);
                setNewTitle("");
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="row-action"
            onClick={() => setCreating(true)}
          >
            + New series
          </button>
        )}
      </div>
    </div>
  );
}
